import React from "react"
import { composeSx } from "../theme/composeSx"
import {
  Button,
  CircularProgress,
  type ButtonProps,
  type Theme,
} from "@mui/material"
import { colors } from "../theme/tokens"
import { resolveViraPalette } from "../theme/resolveViraPalette"
import { AccentDot } from "./AccentDot"

export type ViraButtonVariant = "primary" | "secondary" | "ghost" | "danger"
export type ViraButtonSize = "sm" | "md" | "lg"

export type ViraButtonProps = Omit<
  ButtonProps,
  "variant" | "color" | "size"
> & {
  variant?: ViraButtonVariant
  size?: ViraButtonSize
  loading?: boolean
  showAccentDot?: boolean
}

const SIZE_SX: Record<ViraButtonSize, object> = {
  sm: { px: 1.75, py: 0.75, fontSize: 12 },
  md: { px: 2.5, py: 1.25, fontSize: 13 },
  lg: { px: 3, py: 1.5, fontSize: 15 },
}

function variantConfig(
  theme: Theme,
  variant: ViraButtonVariant,
): { muiVariant: ButtonProps["variant"]; sx: object } {
  const vira = resolveViraPalette(theme)
  const isDark = theme.palette.mode === "dark"
  switch (variant) {
    case "primary":
      return {
        muiVariant: "contained",
        sx: {
          backgroundColor: vira.primaryBtn,
          color: isDark ? colors.neutral.space : colors.neutral.white,
          borderRadius: "7px",
          fontWeight: 600,
          letterSpacing: "0.26px",
          "&:hover": {
            backgroundColor: isDark
              ? colors.primary.ice
              : colors.primary.midnight,
          },
          "&.Mui-disabled": {
            backgroundColor: vira.lavender,
            color: vira.disabledText,
          },
        },
      }
    case "secondary":
      return {
        muiVariant: "outlined",
        sx: {
          backgroundColor: theme.palette.background.paper,
          borderColor: vira.softBorder,
          color: vira.muted,
          borderRadius: "7px",
          fontWeight: 500,
          "&:hover": {
            borderColor: vira.border,
            backgroundColor: vira.pageBg,
          },
          "&.Mui-disabled": {
            backgroundColor: vira.softSurface,
            color: vira.disabledText,
            borderColor: vira.border,
          },
        },
      }
    case "ghost":
      return {
        muiVariant: "text",
        sx: {
          color: vira.lavenderText,
          fontWeight: 600,
          fontSize: 12,
          textDecoration: "underline",
          textUnderlineOffset: 2,
          "&:hover": { backgroundColor: "transparent", opacity: 0.85 },
        },
      }
    case "danger":
      return {
        muiVariant: "contained",
        sx: {
          backgroundColor: colors.secondary.raspberry,
          color: colors.neutral.white,
          borderRadius: "7px",
          fontWeight: 600,
          letterSpacing: "0.26px",
          "&:hover": {
            backgroundColor: colors.secondary.crimson,
          },
          "&.Mui-disabled": {
            backgroundColor: vira.lavender,
            color: vira.disabledText,
          },
        },
      }
  }
}

/** Primary Vira button variants; safe outside `ViraThemeProvider`. */
export function ViraButton({
  variant = "primary",
  size = "md",
  loading = false,
  showAccentDot = false,
  disabled,
  children,
  sx,
  startIcon,
  ...rest
}: ViraButtonProps) {
  const sizeSx = variant === "ghost" ? {} : SIZE_SX[size]
  // Resolve MUI variant from light theme path (same mapping in dark)
  const muiVariant =
    variant === "secondary"
      ? "outlined"
      : variant === "ghost"
        ? "text"
        : "contained"

  return (
    <Button
      variant={muiVariant}
      disabled={disabled || loading}
      startIcon={
        loading ? (
          <CircularProgress size={16} color="inherit" />
        ) : showAccentDot ? (
          <AccentDot soft size={9} />
        ) : (
          startIcon
        )
      }
      sx={composeSx(
        (theme) => ({
          ...variantConfig(theme, variant).sx,
          ...sizeSx,
        }),
        sx,
      )}
      {...rest}
    >
      {children}
    </Button>
  )
}
